// src/components/costs/UnternehmerPicker.jsx
// Auswahl EINES CRM-Eintrags — Firma (Tabelle projectPartners) oder optional
// Person (Tabelle persons) — als Unternehmer eines Vertrags, Auftraggeberin oder
// Adressat einer Zahlungsanweisung (US-KOST-022). Gleiches Prinzip wie
// PartnerAssignField: Suche zeigt passende CRM-Eintraege, fehlt der Treffer,
// laesst sich die Firma/Person direkt im CRM erfassen (PendenzResponsibleCreateModal,
// Relation zum verknuepften Projekt). Anders als PartnerAssignField wird hier
// KEINE Projekt-Relation verwaltet, sondern nur ein Wert {id, name} zurueckgegeben.
import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import PendenzResponsibleCreateModal from "../PendenzResponsibleCreateModal";
import { RELATION_TYPES } from "../../services/azureRelationsService";
import "../../styles/pendenzen.scss";
import "../../styles/_partner-assign-field.scss";

// Anzeigeadresse einer CRM-Firma (mehrzeilig) fuer das PDF.
export function partnerAdresse(p) {
  if (!p) return "";
  const lines = [p.name];
  const strasse = p.addressStrasse || p.address || "";
  if (strasse) lines.push(strasse);
  const ort = [p.addressPlz, p.addressOrt].filter(Boolean).join(" ");
  if (ort) lines.push(ort);
  return lines.filter(Boolean).join("\n");
}

export const personName = (p) => `${p?.firstName || ""} ${p?.lastName || ""}`.trim();

// Anzeigeadresse einer CRM-Person: Organisation, Name, Adresse der Person —
// fehlt diese, die Adresse ihrer Organisation (Namensabgleich in `partners`).
export function personAdresse(p, partners = []) {
  if (!p) return "";
  const org = p.organization
    ? partners.find((x) => (x.name || "").trim().toLowerCase() === p.organization.trim().toLowerCase())
    : null;
  const lines = [];
  if (p.organization) lines.push(p.organization);
  lines.push(personName(p));
  const strasse = p.addressStrasse || org?.addressStrasse || org?.address || "";
  const ort = [p.addressPlz || org?.addressPlz, p.addressOrt || org?.addressOrt].filter(Boolean).join(" ");
  if (strasse) lines.push(strasse);
  if (ort) lines.push(ort);
  return lines.filter(Boolean).join("\n");
}

const UnternehmerPicker = ({
  value = null, // {id, name, kind?} | null
  // (entity|null, meta) => void — entity = vollstaendiges CRM-Objekt;
  // meta.kind = "partner" | "person"; meta.created = true, wenn der Eintrag
  // soeben im CRM neu erfasst wurde (dann hat das CRM-Modal die Projekt-
  // Relation bereits angelegt).
  onChange,
  partners = [],
  // Optional: CRM-Personen zusaetzlich anbieten (Adressat/Ansprechperson).
  persons = [],
  onPartnersReload, // async () => void, nach Neuerfassung
  projectCode = "", // fuer die CRM-Neuerfassung (Relation Projekt <-> Eintrag)
  // Rolle der Projekt-Relation bei Firmen-Neuerfassung (Default Unternehmer/
  // Contractor; im Zahlungsanweisungs-Modal auch AUFTRAGGEBERIN/PROJECT_PARTNER).
  relationType = RELATION_TYPES.CONTRACTOR,
  // Rolle bei Personen-Neuerfassung; undefined = Default des CRM-Modals (PROJECT_PERSON).
  personRelationType,
  createLabel = "als neue Firma im CRM erfassen",
  // Gesetzt = Personen duerfen neu erfasst werden (zweiter Eintrag im Dropdown).
  personCreateLabel = null,
  disabled = false,
  placeholder = "Unternehmer im CRM suchen oder neu erfassen…",
  autoFocus = false,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [createModal, setCreateModal] = useState(null); // { initialName, type }
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  // Dropdown fixed positionieren (wie PartnerAssignField), damit es in
  // Panels/Modals mit overflow nicht abgeschnitten wird.
  useLayoutEffect(() => {
    if (!open) {
      setDropPos(null);
      return undefined;
    }
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 260) });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const q = query.trim().toLowerCase();

  const partnerMatches = useMemo(() => {
    if (!q) return [];
    return partners.filter((p) => (p.name || "").toLowerCase().includes(q)).slice(0, 15);
  }, [partners, q]);

  const personMatches = useMemo(() => {
    if (!q || !persons.length) return [];
    return persons
      .filter((p) => `${personName(p)} ${p.organization || ""}`.toLowerCase().includes(q))
      .slice(0, 10);
  }, [persons, q]);

  const exactMatch = useMemo(
    () =>
      partners.some((p) => (p.name || "").trim().toLowerCase() === q) ||
      persons.some((p) => personName(p).toLowerCase() === q),
    [partners, persons, q]
  );

  const pick = (entity, meta = {}) => {
    setOpen(false);
    setQuery("");
    onChange?.(entity, meta);
  };

  const openCreate = (type) => {
    if (!projectCode) return;
    setOpen(false);
    setCreateModal({ initialName: query.trim(), type });
  };

  return (
    <div className="partner-assign-field unternehmer-picker">
      {value ? (
        <span className="partner-assign-field__chip">
          <span className="partner-assign-field__chip-name">{value.name}</span>
          {value.kind === "person" && <em className="partner-assign-field__chip-role">Person</em>}
          {!disabled && (
            <button type="button" onClick={() => onChange?.(null, {})} aria-label={`${value.name} entfernen`}>
              ×
            </button>
          )}
        </span>
      ) : (
        !disabled && (
          <div className="partner-assign-field__input-wrap" ref={wrapRef}>
            <input
              type="text"
              value={query}
              placeholder={placeholder}
              autoFocus={autoFocus}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
            />
            {open && q && (
              <ul
                className="partner-assign-field__dropdown"
                style={
                  dropPos
                    ? { position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, right: "auto" }
                    : undefined
                }
              >
                {partnerMatches.map((p) => (
                  <li key={`org-${p.id}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(p, { kind: "partner" });
                      }}
                    >
                      <span>{p.name}</span>
                      <span className="partner-assign-field__dropdown-hint">
                        {[p.addressPlz, p.addressOrt].filter(Boolean).join(" ") || "Firma"}
                      </span>
                    </button>
                  </li>
                ))}
                {personMatches.map((p) => (
                  <li key={`person-${p.id}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pick(p, { kind: "person" });
                      }}
                    >
                      <span>{personName(p)}</span>
                      <span className="partner-assign-field__dropdown-hint">{p.organization || "Person"}</span>
                    </button>
                  </li>
                ))}
                {!exactMatch && (
                  <li>
                    <button
                      type="button"
                      className="partner-assign-field__dropdown-create"
                      disabled={!projectCode}
                      title={projectCode ? "" : "Neuerfassung nur mit verknüpftem Projekt möglich"}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        openCreate("organization");
                      }}
                    >
                      + „{query.trim()}" {createLabel}
                    </button>
                  </li>
                )}
                {!exactMatch && personCreateLabel && (
                  <li>
                    <button
                      type="button"
                      className="partner-assign-field__dropdown-create"
                      disabled={!projectCode}
                      title={projectCode ? "" : "Neuerfassung nur mit verknüpftem Projekt möglich"}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        openCreate("person");
                      }}
                    >
                      + „{query.trim()}" {personCreateLabel}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )
      )}

      {/* Das CRM-Modal per Portal an den body haengen: Im DOM innerhalb des
          Panel-Formulars (.control-detail-form, flex) oder des ZA-Modals
          (.estimate-modal .form-group) wuerden dessen Nachfahren-Styles die
          Felder verzerren (z.B. wird `flex: 1 1 160px` in einer Flex-Spalte
          zur Hoehe). Der Wrapper hebt den z-index ueber den Vollbild-Editor. */}
      {createModal &&
        createPortal(
          <div className="crm-modal-portal">
            <PendenzResponsibleCreateModal
              type={createModal.type}
              initialName={createModal.initialName}
              partners={partners}
              projectCode={projectCode}
              relationType={createModal.type === "person" ? personRelationType : relationType}
              onCreated={async ({ entity }) => {
                setCreateModal(null);
                try {
                  await onPartnersReload?.();
                } catch {
                  /* Liste wird beim naechsten Oeffnen nachgeladen */
                }
                // createPartner/createPerson liefern das gespeicherte Objekt (id, Name, Adresse)
                pick(entity, { created: true, kind: createModal.type === "person" ? "person" : "partner" });
              }}
              onClose={() => setCreateModal(null)}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default UnternehmerPicker;
